import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addComplaintReception,
  editComplaintReception,
  fetchComplaintReceptionById,
} from "services/complaintService";
import {
  fetchAdminUnitDetail,
  fetchDepartmentsForAdminUnit,
} from "services/orgService";
import {
  AdminUnitDepartments,
  ComplaintReception,
  ComplaintStatus,
} from "types";
import { LoadingIcon } from "utils/constant";
import {
  errorHandler,
  errorHandlerOnForm,
  formatPhoneNumber,
} from "utils/helper";
import FormInput, { FormSelect, FormTextArea } from "../FormInput";
import EvidenceTab from "./fragment/EvidentTab";
import PowerOfAgencyTab from "./fragment/PowerOfAgencyTab";
import WitnessTab from "./fragment/WitnessTab";

const defaultData: ComplaintReception = {
  user_id: "",
  clerk_name: "",
  clerk_user_id: "",
  manager_name: "",
  manager_user_id: "",
  incident_admin_unit_id: "",
  incident_admin_unit_name: "",
  incident_org_unit_id: "",
  admin_unit_id: "",
  org_unit_id: "",
  departement_id: "",
  unit_name: "",
  service_place_region: "",
  service_place_zone_or_city: "",
  service_place_woreda_or_kebele: "",
  is_appeal: false,
  client_full_name: "",
  region: "",
  zone_or_city: "",
  woreda_or_kebele: "",
  phone_number: "",
  email: "",
  nationality: "",
  accused_person_name: "",
  accused_department: "",
  complaint_summary: "",
  requested_resolution: "",
  status: "forwarded_to_management",

  sub_city: "",
  kebele: "",
  house_number: "",

  application_number: "",
  is_complaint_not_by_mobile: true,
};

const ComplaintRegister: React.FC = () => {
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [formState, setFormState] = useState<ComplaintReception>(defaultData);
  const [savedComplaint, setSavedComplaint] = useState<ComplaintReception>();
  const [departments, setDepartments] = useState<AdminUnitDepartments>();
  const [loading, setLoading] = useState(false);
  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"basic" | "complaint">("basic");

  const fetchData = (complaintId: string | number) => {
    setLoading(true);
    fetchComplaintReceptionById(complaintId)
      .then((data) => {
        setSavedComplaint(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  const fetchOrgRelatedData = async () => {
    const [adminUnitDetailRes, depByAdminUnit] = await Promise.all([
      fetchAdminUnitDetail(loginData?.admin_unit as number),
      fetchDepartmentsForAdminUnit(loginData?.admin_unit as number),
    ]);

    if (adminUnitDetailRes && depByAdminUnit) {
      setDepartments(depByAdminUnit);
      setFormState((prev) => ({
        ...prev,
        incident_admin_unit_id: loginData?.admin_unit + "",
        incident_admin_unit_name: adminUnitDetailRes?.unit_name,
        incident_org_unit_id: loginData?.org_unit + "",
        admin_unit_id: loginData?.admin_unit + "",
        org_unit_id: loginData?.org_unit + "",
      }));
    }
  };

  useEffect(() => {
    if (loginData) {
      fetchOrgRelatedData();
    }
  }, [loginData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = (tab = "all") => {
    if (tab === "basic" || tab === "all") {
      if (!formState.client_full_name) return "Client Full Name is Required.";
      if (!formState.phone_number) return "Phone Number is Required.";
      if (formState?.phone_number.length < 10)
        return "Please Enter a Correct Phone Number.";
      if (formState.email && !/\S+@\S+\.\S+/.test(formState.email))
        return "Please Enter a Valid Email Address.";
      if (!formState?.application_number)
        return "Application Number is Required.";
    }

    if (tab === "complaint" || tab === "all") {
      if (!formState.departement_id) return "Department is Required.";
      if (!formState.complaint_reception_date)
        return "Complaint Reception Date is Required.";
      if (!formState.complaint_summary) return "Complaint Summary is Required.";
      if (new Date(formState?.complaint_reception_date) > new Date()) {
        return `Compliant Reception Date must be on or before ${new Date().toLocaleDateString()}`;
      }
      if (
        formState?.incident_date &&
        new Date(formState?.incident_date) > new Date()
      ) {
        return `Incident Date must be on or before ${new Date().toLocaleDateString()}`;
      }

      // if (!formState.departement_id) return "Department is Required.";
    }

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm("all");
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setActionIsLoading(true);

    addComplaintReception(formState)
      .then((data) => {
        showTooltip("Complaint Successfully Submitted!", "success");
        setSavedComplaint(data as ComplaintReception);
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setActionIsLoading(false));
  };

  const handleStatusChange = (status: ComplaintReception["status"]) => {
    editComplaintReception({
      id: savedComplaint?.id,
      status: status,
    })
      .then(() => {
        showTooltip("Complaint Forwarded Successfully!", "success");
        navigate("/complaint");
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const handleNext = () => {
    const error = validateForm("basic");
    if (error) {
      showTooltip(error, "error");
      return;
    }
    setActiveTab("complaint");
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setActiveTab("basic");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <Loading />;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900">
                Register New Complaint
              </h1>
              <p className="text-gray-600 mt-2">
                Fill in the details below to register a new complaint. Ensure
                all required fields are completed.
              </p>
            </div>
            {savedComplaint && (
              <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-sm font-medium text-red-700">
                  Application Number:{" "}
                </span>
                <span className="font-bold text-red-900">
                  {savedComplaint?.client_id ?? "Pending"}
                </span>
              </div>
            )}
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center mb-8">
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeTab === "basic"
                    ? "bg-red-600 text-white"
                    : "bg-green-100 text-green-600"
                }`}
              >
                1
              </div>
              <div
                className={`ml-2 text-sm font-medium ${
                  activeTab === "basic" ? "text-red-600" : "text-gray-600"
                }`}
              >
                Victim Information
              </div>
            </div>
            <div className="flex-1 h-px bg-gray-300 mx-4"></div>
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeTab === "complaint"
                    ? "bg-red-600 text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                2
              </div>
              <div
                className={`ml-2 text-sm font-medium ${
                  activeTab === "complaint" ? "text-red-600" : "text-gray-600"
                }`}
              >
                Complaint Details
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Main Form */}
          <div className="lg:w-2/3">
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <form onSubmit={handleSubmit}>
                {/* Form Tabs */}
                <div className="flex border-b border-gray-200 mb-8">
                  <button
                    type="button"
                    className={`px-4 py-3 font-medium text-sm md:text-base transition-colors ${
                      activeTab === "basic"
                        ? "text-red-600 border-b-2 border-red-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab("basic")}
                  >
                    Victim Information
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-3 font-medium text-sm md:text-base transition-colors ${
                      activeTab === "complaint"
                        ? "text-red-600 border-b-2 border-red-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => {
                      const error = validateForm("basic");
                      if (error) {
                        showTooltip(error, "error");
                      } else {
                        setActiveTab("complaint");
                      }
                    }}
                  >
                    Complaint Details
                  </button>
                </div>

                {/* Victim Information Tab */}
                {activeTab === "basic" && (
                  <div className="space-y-6 animate-fadeIn">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="w-2 h-5 bg-red-600 mr-2 rounded"></span>
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormInput
                          label="Client Full Name *"
                          type="text"
                          value={formState.client_full_name || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "client_full_name" },
                            })
                          }
                          placeholder="Enter client full name"
                        />
                        <FormSelect
                          label="Gender"
                          value={
                            formState.gender ? String(formState.gender) : ""
                          }
                          onChange={(e) => {
                            setFormState((prev) => ({
                              ...prev,
                              gender:
                                e.target.value === ""
                                  ? undefined
                                  : (e.target.value as "male" | "female"),
                            }));
                          }}
                          options={[
                            { value: "", label: "Select Gender" },
                            { value: "male", label: "Male" },
                            { value: "female", label: "Female" },
                          ]}
                        />
                        <FormInput
                          label="Phone Number *"
                          type="text"
                          value={formState.phone_number || ""}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);

                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "phone_number",
                                value: formatted,
                              },
                            });
                          }}
                          placeholder="Enter phone number"
                        />
                        <FormInput
                          label="Email"
                          type="email"
                          value={formState.email || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "email" },
                            })
                          }
                          placeholder="Enter email address"
                        />
                        <FormInput
                          label="Nationality"
                          type="text"
                          value={formState.nationality || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "nationality" },
                            })
                          }
                          placeholder="Enter nationality"
                        />
                        <FormInput
                          label="Application Number *"
                          type="text"
                          value={formState.application_number || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "application_number",
                              },
                            })
                          }
                          placeholder="Enter application number"
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="w-2 h-5 bg-red-600 mr-2 rounded"></span>
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FormInput
                          label="Region"
                          type="text"
                          value={formState.region || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "region" },
                            })
                          }
                          placeholder="Enter region"
                        />
                        <FormInput
                          label="Zone/City"
                          type="text"
                          value={formState.zone_or_city || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "zone_or_city" },
                            })
                          }
                          placeholder="Enter zone or city"
                        />
                        <FormInput
                          label="Woredas"
                          type="text"
                          value={formState.woreda_or_kebele || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "woreda_or_kebele" },
                            })
                          }
                          placeholder="Enter woreda"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-3">
                        <FormInput
                          label="Sub City"
                          type="text"
                          value={formState.sub_city || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "sub_city" },
                            })
                          }
                          placeholder="Enter sub city"
                        />
                        <FormInput
                          label="Kebele"
                          type="text"
                          value={formState.kebele || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "kebele" },
                            })
                          }
                          placeholder="Enter kebele"
                        />
                        <FormInput
                          label="House Number"
                          type="text"
                          value={formState.house_number || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "house_number" },
                            })
                          }
                          placeholder="Enter house number"
                        />
                      </div>
                    </div>

                    {/* Next Button for Victim Information Tab */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-8 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                      >
                        Next
                        <svg
                          className="w-5 h-5 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Complaint Details Tab */}
                {activeTab === "complaint" && (
                  <div className="space-y-6 animate-fadeIn">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="w-2 h-5 bg-red-600 mr-2 rounded"></span>
                        Incident Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormInput
                          label="Complaint Reception Date *"
                          type="date"
                          value={
                            formState.complaint_reception_date
                              ? new Date(formState.complaint_reception_date)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "complaint_reception_date",
                              },
                            })
                          }
                        />
                        <FormInput
                          label="Incident Date"
                          type="date"
                          value={
                            formState.incident_date
                              ? new Date(formState.incident_date)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: { ...e.target, name: "incident_date" },
                            })
                          }
                        />
                        <FormInput
                          label="Accused Person Name"
                          type="text"
                          value={formState.accused_person_name || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "accused_person_name",
                              },
                            })
                          }
                          placeholder="Enter accused person name"
                        />
                        {/* <FormSelect
                          label="Accused Department"
                          value={formState.accused_department || ""}
                          onChange={(e) => {
                            setFormState((prev) => ({
                              ...prev,
                              accused_department:
                                e.target.value === ""
                                  ? undefined
                                  : (e.target.value as
                                      | "DRAL"
                                      | "Asset Recovery"
                                      | "Prosecution"
                                      | "Civil"),
                            }));
                          }}
                          options={Object.entries(AccusedDepartment).map(
                            ([field, errors]) => ({
                              value: field ? String(field) : "",
                              label: field ? String(field) : "",
                            }),
                          )}
                        /> */}
                        <FormSelect
                          label="Department *"
                          value={formState.departement_id || ""}
                          onChange={(e) => {
                            setFormState((prev) => ({
                              ...prev,
                              departement_id: e.target.value,
                              unit_name:
                                e.target.options[e.target.selectedIndex].text,
                            }));
                          }}
                          options={(departments?.tree || []).map((field) => ({
                            value: String(field?.uu_id),
                            label: field?.unit_name,
                          }))}
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="w-2 h-5 bg-red-600 mr-2 rounded"></span>
                        Complaint Description
                      </h3>
                      <div className="space-y-6">
                        <FormTextArea
                          label="Complaint Summary *"
                          value={formState.complaint_summary || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "complaint_summary",
                              },
                            })
                          }
                          placeholder="Provide a detailed summary of the complaint..."
                          rows={5}
                        />
                        <FormTextArea
                          label="Requested Resolution"
                          value={formState.requested_resolution || ""}
                          onChange={(e) =>
                            handleChange({
                              ...e,
                              target: {
                                ...e.target,
                                name: "requested_resolution",
                              },
                            })
                          }
                          placeholder="What resolution is the client seeking?"
                          rows={4}
                        />
                      </div>
                    </div>

                    {/* Form Actions for Complaint Details Tab */}
                    {!savedComplaint && (
                      <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-8 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                        >
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          Back
                        </button>
                        <button
                          type="button"
                          disabled={actionIsLoading}
                          onClick={() => navigate(-1)}
                          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={actionIsLoading}
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                        >
                          {actionIsLoading ? (
                            <>
                              <span className="animate-spin">
                                {LoadingIcon}
                              </span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-5 h-5 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Register Complaint
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            {/* Additional Information (when complaint is saved) */}
            {savedComplaint && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <svg
                    className="w-6 h-6 mr-2 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Complaint Registered Successfully!
                </h3>
                <p className="text-gray-600 mb-6">
                  You can now add additional information such as evidence,
                  witnesses, and power of agency details.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="text-sm text-red-600 font-medium">
                      Status
                    </div>
                    <div className="mt-1 font-semibold text-red-900">
                      {savedComplaint.status &&
                        ComplaintStatus[savedComplaint.status]}
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-600 font-medium">
                      Date Registered
                    </div>
                    <div className="mt-1 font-semibold text-green-900">
                      {new Date(
                        savedComplaint.complaint_reception_date || "",
                      ).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-sm text-purple-600 font-medium">
                      Application Number
                    </div>
                    <div className="mt-1 font-semibold text-purple-900">
                      {savedComplaint.client_id || "Pending"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Additional Tabs */}
          <div className="lg:w-1/3 space-y-6">
            {savedComplaint && (
              <>
                <PowerOfAgencyTab
                  compliant={savedComplaint}
                  setRefetch={() => fetchData(savedComplaint?.id || "")}
                  canUpdate={true}
                />
                <EvidenceTab
                  compliant={savedComplaint}
                  setRefetch={() => fetchData(savedComplaint?.id || "")}
                  canUpdate={true}
                />
                <WitnessTab
                  compliant={savedComplaint}
                  setRefetch={() => fetchData(savedComplaint?.id || "")}
                  canUpdate={true}
                />

                {/* <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Ready to Proceed?
                    </h3>
                    <p className="text-red-100 mb-6">
                      Once all information is complete, forward this complaint
                      to management for review.
                    </p>
                    <button
                      onClick={() =>
                        handleStatusChange("forwarded_to_management")
                      }
                      className="w-full px-4 py-3 bg-white text-red-700 font-semibold rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      Forward to Management
                    </button>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintRegister;
